import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useReactToPrint } from 'react-to-print';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Button, Chip, CircularProgress,
  Alert, TextField, IconButton, Tooltip, Select, MenuItem, FormControl,
  InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  Tabs, Tab, Container, Checkbox, FormControlLabel, Snackbar, Avatar, Divider
} from '@mui/material';
import {
  Visibility, Print, Search, LocalHospital, Event,
  PersonAdd, Hotel, MoreVert, ExitToApp, EventAvailable,
  CloudUpload, Description, Close, Image, PictureAsPdf, MedicalServices, Receipt, EventNote,
  Edit, Save, Cancel, MeetingRoom, PhotoCamera, Delete, DocumentScanner, CheckCircle
} from '@mui/icons-material';
import { patientAPI, ipdAPI, consultationAPI, testReportsAPI, visitAPI, billingAPI, dayCareAPI } from '../services/api';
import DischargeSummaryPrint from './DischargeSummaryPrint';
import OPDSlipPrint from './OPDSlipPrint';
import TodaysRecordPrint from './TodaysRecordPrint';
import PatientFilePrint from './PatientFilePrint';
import PatientRegistration from './PatientRegistration';
import OPDRegistration from './OPDRegistration';
import IPDAdmission from './IPDAdmission';
import CameraCapture from './CameraCapture';
import DayCarePatientsList from './doctor/DayCarePatientsList';
import { useNavigate } from 'react-router-dom';

function PatientList() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [ipdAdmissions, setIpdAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [consultationDialog, setConsultationDialog] = useState(false);
  const [printDialog, setPrintDialog] = useState(false);
  const [todaysPrintDialog, setTodaysPrintDialog] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPatientForAction, setSelectedPatientForAction] = useState(null);
  const [actionsDialog, setActionsDialog] = useState(false);
  const [detailsTab, setDetailsTab] = useState(0);
  const [patientDetailBills, setPatientDetailBills] = useState([]);
  const [patientDetailBillsLoading, setPatientDetailBillsLoading] = useState(false);
  const [patientDetailDayCare, setPatientDetailDayCare] = useState([]);
  const [patientDetailDayCareLoading, setPatientDetailDayCareLoading] = useState(false);
  const [opdDialog, setOpdDialog] = useState(false);
  const [ipdDialog, setIpdDialog] = useState(false);
  const [success, setSuccess] = useState('');

  // Change Room (IPD admissions tab)
  const [changeRoomDialog, setChangeRoomDialog] = useState({ open: false, admission: null });
  const [changeRoomRooms, setChangeRoomRooms] = useState([]);
  const [changeRoomSelectedId, setChangeRoomSelectedId] = useState('');
  const [changeRoomLoading, setChangeRoomLoading] = useState(false);

  // Test reports dialog state
  const [uploadReportDialog, setUploadReportDialog] = useState({
    open: false,
    patient: null,
    files: [],
    reportType: 'General',
    testDate: new Date().toISOString().split('T')[0],
    notes: '',
    uploading: false
  });

  const [viewReportsDialog, setViewReportsDialog] = useState({
    open: false,
    patient: null,
    reports: [],
    loading: false
  });

  const [reportViewerDialog, setReportViewerDialog] = useState({
    open: false,
    reportUrl: null,
    reportName: '',
    mimeType: ''
  });

  // Edit patient state
  const [editPatientMode, setEditPatientMode] = useState(false);
  const [editPatientForm, setEditPatientForm] = useState({});
  const [editPatientLoading, setEditPatientLoading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [aadharFrontPreview, setAadharFrontPreview] = useState(null);
  const [aadharBackPreview, setAadharBackPreview] = useState(null);
  const [scanningFront, setScanningFront] = useState(false);
  const [scanningBack, setScanningBack] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [imageViewerDialog, setImageViewerDialog] = useState({ open: false, src: '', title: '' });

  const frontInputRef = useRef();
  const backInputRef = useRef();

  // Discharge dialog state
  const [dischargeDialog, setDischargeDialog] = useState({
    open: false,
    admission: null,
    dischargeReason: '',
    isBlocked: false,
    processing: false
  });

  // Extend discharge dialog state
  const [extendDialog, setExtendDialog] = useState({
    open: false,
    admission: null,
    additionalDays: 7,
    reason: '',
    processing: false
  });

  // Ref for printing consultation
  const consultationPrintRef = useRef();
  const [consultationVitals, setConsultationVitals] = useState(null);
  const [consultationVisitData, setConsultationVisitData] = useState(null);

  // Daily plan print
  const dailyPlanPrintRef = useRef();
  const [dailyPlanDialog, setDailyPlanDialog] = useState({ open: false, patientId: null, patientName: '', date: new Date().toISOString().split('T')[0] });
  const [dailyPlanData, setDailyPlanData] = useState(null);
  const [dailyPlanLoading, setDailyPlanLoading] = useState(false);
  const handlePrintDailyPlan = useReactToPrint({
    contentRef: dailyPlanPrintRef,
    documentTitle: 'Patient Daily Plan',
    pageStyle: '@page { size: A4 portrait; margin: 10mm; }'
  });

  const fetchAndPrintDailyPlan = async () => {
    setDailyPlanLoading(true);
    try {
      const res = await patientAPI.getDailyPlan(dailyPlanDialog.patientId, dailyPlanDialog.date);
      setDailyPlanData(res.data.data);
      setTimeout(() => handlePrintDailyPlan(), 300);
    } catch (err) {
      console.error('Failed to load daily plan', err);
    } finally {
      setDailyPlanLoading(false);
    }
  };

  // Patient File Print
  const [filePrintDialog, setFilePrintDialog] = useState({ open: false, admissionId: null, patientId: null });

  // Get user info to check if reception
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isReception = user.role === 'reception';
  const isDoctor = user.role === 'doctor';

  useEffect(() => {
    if (activeTab === 0) {
      loadPatients();
    } else if ((activeTab === 1 && isDoctor) || (activeTab === 2 && isReception)) {
      loadConsultations();
    } else if (activeTab === 3 && isReception) {
      loadIPDAdmissions();
    }
  }, [statusFilter, activeTab]);

  const loadPatients = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (searchQuery) {
        params.searchQuery = searchQuery;
      }

      const response = await patientAPI.getAll(params);
      setPatients(response.data.data || []);
    } catch (err) {
      setError('Failed to load patients');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadConsultations = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) {
        params.searchQuery = searchQuery;
      }

      const response = isDoctor
        ? await consultationAPI.getMyConsultations(params)
        : await consultationAPI.getAll(params);

      setConsultations(response.data.data || []);
    } catch (err) {
      setError('Failed to load consultations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadIPDAdmissions = async () => {
    setLoading(true);
    try {
      const response = await ipdAPI.getActiveAdmissions();
      setIpdAdmissions(response.data.data || response.data);
    } catch (err) {
      setError('Failed to load IPD admissions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (activeTab === 0) {
      loadPatients();
    } else if ((activeTab === 1 && isDoctor) || (activeTab === 2 && isReception)) {
      loadConsultations();
    }
  };

  const handleViewDetails = async (patientId) => {
    try {
      setDetailsTab(0);
      setEditPatientMode(false);
      setPatientDetailBills([]);
      setPatientDetailDayCare([]);
      const response = await patientAPI.getPatientDetails(patientId);
      setSelectedPatient(response.data.data);
      setDetailsDialog(true);
    } catch (err) {
      setError('Failed to load patient details');
      console.error(err);
    }
  };

  const openEditPatient = () => {
    const p = selectedPatient.patient;
    setEditPatientForm({
      firstName: p.first_name || '',
      lastName: p.last_name || '',
      phone: p.phone || '',
      email: p.email || '',
      address: p.address || '',
      dateOfBirth: p.date_of_birth ? p.date_of_birth.split('T')[0] : '',
      gender: p.gender || '',
      aadharNumber: p.aadhar_number || '',
      fatherHusbandName: p.father_husband_name || '',
      emergencyContactName: p.emergency_contact_name || '',
      emergencyContactPhone: p.emergency_contact_phone || '',
      photoBlob: p.photo_blob || '',
      photoMimeType: p.photo_mime_type || '',
      aadharFrontImage: p.aadhar_front_image || null,
      aadharBackImage: p.aadhar_back_image || null
    });
    setAadharFrontPreview(p.aadhar_front_image || null);
    setAadharBackPreview(p.aadhar_back_image || null);
    setScanResult(null);
    setEditPatientMode(true);
  };

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleAadharScan = async (file, side) => {
    if (!file) return;
    const setScanningState = side === 'front' ? setScanningFront : setScanningBack;
    const setPreview = side === 'front' ? setAadharFrontPreview : setAadharBackPreview;

    try {
      setScanningState(true);
      const base64 = await readFileAsBase64(file);
      setPreview(base64);

      const mimeType = file.type || 'image/jpeg';
      const res = await patientAPI.scanAadhar(base64, mimeType);
      const data = res.data.data;

      setScanResult(data);

      // Prefill form fields from scanned data
      setEditPatientForm(prev => ({
        ...prev,
        firstName: data.name ? data.name.split(' ')[0] : prev.firstName,
        lastName: data.name && data.name.split(' ').length > 1
          ? data.name.split(' ').slice(1).join(' ')
          : prev.lastName,
        dateOfBirth: data.dateOfBirth || prev.dateOfBirth,
        gender: data.gender || prev.gender,
        address: data.address || prev.address,
        aadharNumber: data.aadharNumber || prev.aadharNumber,
        fatherHusbandName: data.fatherHusbandName || prev.fatherHusbandName,
        [side === 'front' ? 'aadharFrontImage' : 'aadharBackImage']: base64
      }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to scan Aadhar card. Please fill details manually.');
    } finally {
      setScanningState(false);
    }
  };

  const handleCameraCapture = (imageData, mimeType) => {
    setEditPatientForm(f => ({ ...f, photoBlob: imageData, photoMimeType: mimeType }));
    setCameraOpen(false);
  };

  const handleRemovePhoto = () => {
    setEditPatientForm(f => ({ ...f, photoBlob: '', photoMimeType: '' }));
  };

  const handleSavePatient = async () => {
    if (editPatientForm.aadharNumber && editPatientForm.aadharNumber.length !== 12) {
      setError('Aadhar number must be exactly 12 digits');
      return;
    }

    setEditPatientLoading(true);
    try {
      await patientAPI.update(selectedPatient.patient.id, {
        ...editPatientForm,
        aadharFrontImage: aadharFrontPreview || null,
        aadharBackImage: aadharBackPreview || null
      });
      // Refresh patient details
      const response = await patientAPI.getPatientDetails(selectedPatient.patient.id);
      setSelectedPatient(response.data.data);
      setEditPatientMode(false);
      setSuccess('Patient details updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update patient');
    } finally {
      setEditPatientLoading(false);
    }
  };

  const handleDetailsTabChange = async (_, newTab) => {
    setDetailsTab(newTab);
    const patientId = selectedPatient?.patient?.id;
    if (!patientId) return;

    if (newTab === 4 && patientDetailDayCare.length === 0) {
      setPatientDetailDayCareLoading(true);
      try {
        const res = await dayCareAPI.getPatientRegistrations(patientId);
        setPatientDetailDayCare(res.data.data || []);
      } catch { /* silent */ }
      setPatientDetailDayCareLoading(false);
    }
    if (newTab === 5 && patientDetailBills.length === 0) {
      setPatientDetailBillsLoading(true);
      try {
        const res = await billingAPI.getPatientBills(patientId);
        setPatientDetailBills(res.data.data || []);
      } catch { /* silent */ }
      setPatientDetailBillsLoading(false);
    }
  };

  const handlePrintSummary = (admissionId) => {
    setSelectedAdmission(admissionId);
    setPrintDialog(true);
  };

  const handleOpenActionsDialog = (patient) => {
    setSelectedPatientForAction(patient);
    setActionsDialog(true);
  };

  const handleCloseActionsDialog = () => {
    setActionsDialog(false);
    setSelectedPatientForAction(null);
  };

  const handleOPDRegistration = () => {
    setActionsDialog(false);
    setOpdDialog(true);
  };

  const handleIPDAdmission = () => {
    setActionsDialog(false);
    setIpdDialog(true);
  };

  const handleDayCareRegistration = () => {
    setActionsDialog(false);
    navigate('/reception/day-care', { state: { preselectedPatient: selectedPatientForAction } });
  };

  const handleDischargePatient = async () => {
    setActionsDialog(false);

    try {
      // First, get the patient details to find the active admission ID
      const response = await patientAPI.getPatientDetails(selectedPatientForAction.id);
      const patientDetails = response.data.data;

      // Find the active admission
      const activeAdmission = patientDetails.admissions.find(a => a.status === 'admitted');

      if (!activeAdmission) {
        setError('No active admission found for this patient');
        return;
      }

      // Open discharge dialog instead of immediate discharge
      setDischargeDialog({
        open: true,
        admission: activeAdmission,
        dischargeReason: '',
        isBlocked: false,
        processing: false
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load admission details. Please try again.');
      console.error('Discharge error:', err);
    }
  };

  const handleOpenDischargeDialog = (admission) => {
    setDischargeDialog({
      open: true,
      admission,
      dischargeReason: '',
      isBlocked: false,
      processing: false
    });
  };

  const handleCloseDischargeDialog = () => {
    if (!dischargeDialog.processing) {
      setDischargeDialog({
        open: false,
        admission: null,
        dischargeReason: '',
        isBlocked: false,
        processing: false
      });
    }
  };

  const handleDischarge = async () => {
    setDischargeDialog(prev => ({ ...prev, processing: true }));

    try {
      await ipdAPI.dischargePatient(dischargeDialog.admission.id, {
        dischargeReason: dischargeDialog.dischargeReason,
        isBlocked: dischargeDialog.isBlocked
      });

      setSuccess(`Patient discharged successfully`);
      handleCloseDischargeDialog();
      loadPatients();
      if (selectedPatient) {
        // Refresh patient details if dialog is open
        handleViewDetails(selectedPatient.patient.id);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to discharge patient. Please try again.');
    } finally {
      setDischargeDialog(prev => ({ ...prev, processing: false }));
    }
  };

  const handleOpenExtendDialog = (admission) => {
    setExtendDialog({
      open: true,
      admission,
      additionalDays: 7,
      reason: '',
      processing: false
    });
  };

  const handleCloseExtendDialog = () => {
    if (!extendDialog.processing) {
      setExtendDialog({
        open: false,
        admission: null,
        additionalDays: 7,
        reason: '',
        processing: false
      });
    }
  };

  const handleExtendDischarge = async () => {
    if (!extendDialog.additionalDays || extendDialog.additionalDays <= 0) {
      setError('Please enter a valid number of additional days');
      return;
    }

    setExtendDialog(prev => ({ ...prev, processing: true }));

    try {
      await ipdAPI.extendDischargeDate(extendDialog.admission.id, {
        additionalDays: extendDialog.additionalDays,
        reason: extendDialog.reason
      });

      setSuccess(`Planned days extended by ${extendDialog.additionalDays} days`);
      handleCloseExtendDialog();
      loadPatients();
      if (selectedPatient) {
        // Refresh patient details if dialog is open
        handleViewDetails(selectedPatient.patient.id);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to extend planned days. Please try again.');
    } finally {
      setExtendDialog(prev => ({ ...prev, processing: false }));
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  const handleViewConsultation = async (consultationId) => {
    try {
      const response = await consultationAPI.getById(consultationId);
      const consultData = response.data.data;
      setSelectedConsultation(consultData);

      // Fetch vitals for OPD slip print
      if (consultData?.consultation?.visit_id) {
        try {
          const visitRes = await visitAPI.getDetails(consultData.consultation.visit_id);
          setConsultationVitals(visitRes.data.data?.vitals || null);
          setConsultationVisitData(visitRes.data.data?.visit || null);
        } catch (e) {
          setConsultationVitals(null);
          setConsultationVisitData(null);
        }
      }

      setConsultationDialog(true);
    } catch (err) {
      setError('Failed to load consultation details');
      console.error(err);
    }
  };

  const handlePrintConsultation = useReactToPrint({
    contentRef: consultationPrintRef,
    documentTitle: 'OPD Slip',
    pageStyle: '@page { size: A4 portrait; margin: 0; } @media print { body { margin: 0; } }',
  });

  const handleIPDAdmissionFromConsultation = () => {
    if (selectedConsultation) {
      // Get patient info from the consultation including visit_id
      const patientInfo = {
        id: selectedConsultation.consultation.patient_id,
        patient_code: selectedConsultation.consultation.patient_code,
        first_name: selectedConsultation.consultation.first_name,
        last_name: selectedConsultation.consultation.last_name,
        phone: selectedConsultation.consultation.phone || '',
        gender: selectedConsultation.consultation.gender || '',
        date_of_birth: selectedConsultation.consultation.date_of_birth || '',
        email: selectedConsultation.consultation.email || '',
        address: selectedConsultation.consultation.address || '',
        visit_id: selectedConsultation.consultation.visit_id // Pre-populate visit ID
      };

      setSelectedPatientForAction(patientInfo);
      setConsultationDialog(false); // Close consultation dialog
      setIpdDialog(true); // Open IPD admission dialog
    }
  };

  const handleDischargeFromIPDTab = async (admission) => {
    if (!window.confirm(`Are you sure you want to discharge ${admission.first_name} ${admission.last_name}?`)) {
      return;
    }

    try {
      await ipdAPI.dischargePatient(admission.id);
      setError('');
      alert(`Patient ${admission.first_name} ${admission.last_name} has been discharged successfully!`);
      loadIPDAdmissions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to discharge patient. Please try again.');
      console.error('Discharge error:', err);
    }
  };

  const openChangeRoomDialog = async (admission) => {
    setChangeRoomSelectedId('');
    setChangeRoomDialog({ open: true, admission });
    try {
      const res = await ipdAPI.getAvailableRooms();
      const rooms = res.data.data || [];
      setChangeRoomRooms(rooms.filter(r => r.room_number !== admission.room_number));
    } catch {
      setError('Failed to load available rooms');
      setChangeRoomDialog({ open: false, admission: null });
    }
  };

  const handleChangeRoom = async () => {
    if (!changeRoomSelectedId) return;
    setChangeRoomLoading(true);
    try {
      await ipdAPI.changeRoom(changeRoomDialog.admission.id, { newRoomId: changeRoomSelectedId });
      setSuccess('Room changed successfully');
      setChangeRoomDialog({ open: false, admission: null });
      loadIPDAdmissions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change room');
    } finally {
      setChangeRoomLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    switch (category.toLowerCase()) {
      case 'hotel': return 'success';
      case 'cottage': return 'primary';
      case 'ward': return 'info';
      default: return 'default';
    }
  };

  const renderConsultations = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LocalHospital sx={{ fontSize: 40, color: 'primary.main' }} />
                <Typography variant="h5" fontWeight="bold">
                  {isDoctor ? 'My Consultations' : 'All Consultations'}
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={9}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Search by patient name, code, or phone"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={handleSearch}>
                        <Search />
                      </IconButton>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={loadConsultations}
                  sx={{ height: '56px' }}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>

            <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>UHID</strong></TableCell>
                    <TableCell><strong>Patient Name</strong></TableCell>
                    {isReception && <TableCell><strong>Doctor</strong></TableCell>}
                    <TableCell><strong>Diagnosis</strong></TableCell>
                    <TableCell><strong>Visit Type</strong></TableCell>
                    <TableCell><strong>Treatments</strong></TableCell>
                    <TableCell><strong>Packages</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {consultations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isReception ? 10 : 9} align="center">
                        <Typography color="text.secondary" sx={{ py: 3 }}>
                          No consultations found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    consultations.map((consultation) => (
                      <TableRow key={consultation.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(consultation.consultation_date).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(consultation.consultation_date).toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {consultation.patient_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {consultation.first_name} {consultation.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {consultation.phone}
                          </Typography>
                        </TableCell>
                        {isReception && (
                          <TableCell>
                            <Typography variant="body2">
                              {consultation.doctor_name}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {consultation.diagnosis || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={consultation.visit_type?.toUpperCase()} size="small" color="primary" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={consultation.prescribed_treatments_count || 0}
                            size="small"
                            color={consultation.prescribed_treatments_count > 0 ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={consultation.prescribed_packages_count || 0}
                            size="small"
                            color={consultation.prescribed_packages_count > 0 ? 'info' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={consultation.status}
                            size="small"
                            color={consultation.status === 'completed' ? 'success' : consultation.status === 'suggested_ipd' ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewConsultation(consultation.id)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderIPDAdmissions = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Hotel sx={{ fontSize: 40, color: 'primary.main' }} />
                <Typography variant="h5" fontWeight="bold">
                  IPD Admitted Patients
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={loadIPDAdmissions}
              >
                Refresh
              </Button>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>UHID</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Room</strong></TableCell>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell><strong>Admission Date</strong></TableCell>
                    <TableCell align="right"><strong>Days</strong></TableCell>
                    <TableCell align="right"><strong>Planned</strong></TableCell>
                    <TableCell align="right"><strong>Daily Charge</strong></TableCell>
                    <TableCell align="right"><strong>Total Charges</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ipdAdmissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography color="text.secondary" sx={{ py: 3 }}>
                          No active IPD admissions
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    ipdAdmissions.map((admission) => (
                      <TableRow key={admission.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {admission.patient_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {admission.first_name} {admission.last_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {admission.room_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={admission.category_name}
                            color={getCategoryColor(admission.category_name)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(admission.admission_date).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {admission.days_admitted}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {admission.planned_days || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            ₹{admission.daily_charge}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            ₹{admission.total_charges}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="View IPD Plans">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => navigate(`/reception/ipd-plans/${admission.id}`, {
                                  state: {
                                    patientCode: admission.patient_code,
                                    patientName: `${admission.first_name} ${admission.last_name}`,
                                    roomNumber: admission.room_number,
                                    admissionDate: admission.admission_date,
                                    plannedDays: admission.planned_days || 0
                                  }
                                })}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Change Room">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => openChangeRoomDialog(admission)}
                              >
                                <MeetingRoom />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Discharge Patient">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDischargeFromIPDTab(admission)}
                              >
                                <ExitToApp />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderTabContent = () => {
    if (isDoctor) {
      // Doctor has only 2 tabs: All Patients and My Consultations
      switch (activeTab) {
        case 0:
          return renderPatientList();
        case 1:
          return renderConsultations();
        default:
          return renderPatientList();
      }
    }

    if (isReception) {
      // Reception has 5 tabs: All Patients, Register New Patient, All Consultations, IPD Admissions, Day Care Admissions
      switch (activeTab) {
        case 0:
          return renderPatientList();
        case 1:
          return <PatientRegistration onSuccess={() => { setActiveTab(0); loadPatients(); }} />;
        case 2:
          return renderConsultations();
        case 3:
          return renderIPDAdmissions();
        case 4:
          return <DayCarePatientsList />;
        default:
          return renderPatientList();
      }
    }

    return renderPatientList();
  };

  const renderPatientList = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LocalHospital sx={{ fontSize: 40, color: 'primary.main' }} />
                <Typography variant="h5" fontWeight="bold">
                  {activeTab === 0 ? 'All Patients' : 'Patient Records'}
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search Patients"
                  placeholder="Search by name, phone, or patient code"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={handleSearch}>
                        <Search />
                      </IconButton>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status Filter"
                  >
                    <MenuItem value="all">All Patients</MenuItem>
                    <MenuItem value="admitted">Currently Admitted (IPD)</MenuItem>
                    <MenuItem value="day_care">In Day Care</MenuItem>
                    <MenuItem value="discharged">Discharged</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={loadPatients}
                  sx={{ height: '56px' }}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>

            <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>UHID</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Age/Gender</strong></TableCell>
                    <TableCell><strong>Contact</strong></TableCell>
                    <TableCell><strong>Visits</strong></TableCell>
                    <TableCell><strong>Admissions</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography color="text.secondary" sx={{ py: 3 }}>
                          No patients found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    patients.map((patient) => (
                      <TableRow key={patient.id} hover
                        sx={patient.active_day_care > 0 ? { bgcolor: '#e0f7fa' } : {}}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {patient.patient_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {patient.first_name} {patient.last_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {calculateAge(patient.date_of_birth)} yrs / {patient.gender}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{patient.phone}</Typography>
                          {patient.email && (
                            <Typography variant="caption" color="text.secondary">
                              {patient.email}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {patient.total_visits || 0} total
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {patient.opd_visits || 0} OPD
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {patient.total_admissions || 0} total
                          </Typography>
                          {patient.active_admissions > 0 && (
                            <Chip
                              label={`${patient.active_admissions} Active`}
                              size="small"
                              color="warning"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {patient.active_admissions > 0 ? (
                            <Chip label="IPD Admitted" color="error" size="small" />
                          ) : patient.active_day_care > 0 ? (
                            <Chip label="In Day Care" size="small"
                              sx={{ bgcolor: '#00838f', color: '#fff', fontWeight: 600 }} />
                          ) : patient.total_admissions > 0 ? (
                            <Chip label="Discharged" color="success" size="small" />
                          ) : (
                            <Chip label="OPD Only" color="info" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Actions">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenActionsDialog(patient)}
                              >
                                <MoreVert />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Patient Actions Dialog */}
        <Dialog
          open={actionsDialog}
          onClose={handleCloseActionsDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {selectedPatientForAction?.first_name} {selectedPatientForAction?.last_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  UHID: {selectedPatientForAction?.patient_code}
                  {selectedPatientForAction?.active_admissions > 0 && (
                    <Chip label="Admitted" color="error" size="small" sx={{ ml: 1 }} />
                  )}
                </Typography>
              </Box>
              <IconButton size="small" onClick={handleCloseActionsDialog}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                fullWidth variant="outlined" startIcon={<Visibility />}
                onClick={() => { handleCloseActionsDialog(); handleViewDetails(selectedPatientForAction?.id); }}
              >
                View Patient Details
              </Button>

              {isReception && (
                <Button fullWidth variant="outlined" startIcon={<LocalHospital />} onClick={handleOPDRegistration}>
                  Register OPD Visit
                </Button>
              )}

              {isReception && (
                selectedPatientForAction?.active_admissions > 0 ? (
                  <Button fullWidth variant="outlined" color="error" startIcon={<ExitToApp />} onClick={handleDischargePatient}>
                    Discharge Patient
                  </Button>
                ) : (
                  <Button fullWidth variant="outlined" startIcon={<Hotel />} onClick={handleIPDAdmission}>
                    IPD Admission
                  </Button>
                )
              )}

              {isReception && (
                <Button fullWidth variant="outlined" startIcon={<MedicalServices />} onClick={handleDayCareRegistration}>
                  Day Care Registration
                </Button>
              )}

              {selectedPatientForAction?.active_admissions > 0 && isReception && (
                <Button
                  fullWidth variant="outlined" startIcon={<Visibility />}
                  onClick={() => { handleCloseActionsDialog(); handleViewDetails(selectedPatientForAction?.id); }}
                >
                  View IPD Plans
                </Button>
              )}

              {selectedPatientForAction?.active_admissions > 0 && isDoctor && (
                <Button
                  fullWidth variant="outlined" startIcon={<MedicalServices />}
                  onClick={async () => {
                    handleCloseActionsDialog();
                    try {
                      const response = await patientAPI.getPatientDetails(selectedPatientForAction.id);
                      const activeAdmission = response.data.data.admissions.find(a => a.status === 'admitted');
                      if (activeAdmission) {
                        navigate(`/doctor/discharge-prescription/${activeAdmission.id}`);
                      }
                    } catch (err) {
                      setError('Failed to load admission details');
                    }
                  }}
                >
                  Discharge Prescription
                </Button>
              )}

              {selectedPatientForAction?.total_admissions > 0 && (
                <Button
                  fullWidth variant="outlined" startIcon={<Print />}
                  onClick={async () => {
                    handleCloseActionsDialog();
                    try {
                      const response = await patientAPI.getPatientDetails(selectedPatientForAction.id);
                      const admissions = response.data.data.admissions || [];
                      const latest = admissions[0];
                      if (latest) handlePrintSummary(latest.id);
                    } catch {
                      setError('Failed to load admission details');
                    }
                  }}
                >
                  Print Discharge Summary
                </Button>
              )}

              {isReception && selectedPatientForAction?.total_admissions > 0 && (
                <Button
                  fullWidth variant="contained" color="primary" startIcon={<Print />}
                  onClick={async () => {
                    handleCloseActionsDialog();
                    try {
                      const response = await patientAPI.getPatientDetails(selectedPatientForAction.id);
                      const admissions = response.data.data.admissions || [];
                      const latest = admissions[0];
                      if (latest) {
                        setFilePrintDialog({
                          open: true,
                          admissionId: latest.id,
                          patientId: selectedPatientForAction.id
                        });
                      }
                    } catch {
                      setError('Failed to load admission details');
                    }
                  }}
                >
                  Print Patient File
                </Button>
              )}

              <Button
                fullWidth variant="outlined" startIcon={<CloudUpload />}
                onClick={() => {
                  setUploadReportDialog({ ...uploadReportDialog, open: true, patient: selectedPatientForAction });
                  handleCloseActionsDialog();
                }}
              >
                Upload Test Reports
              </Button>

              <Button
                fullWidth variant="outlined" startIcon={<Description />}
                onClick={async () => {
                  setViewReportsDialog({ open: true, patient: selectedPatientForAction, reports: [], loading: true });
                  handleCloseActionsDialog();
                  try {
                    const response = await testReportsAPI.getPatientReports(selectedPatientForAction.id);
                    setViewReportsDialog(prev => ({ ...prev, reports: response.data.data || [], loading: false }));
                  } catch (err) {
                    setViewReportsDialog(prev => ({ ...prev, loading: false }));
                    setError('Failed to load test reports');
                  }
                }}
              >
                View Test Reports
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Patient Details Dialog */}
        <Dialog
          open={detailsDialog}
          onClose={() => { setDetailsDialog(false); setEditPatientMode(false); }}
          maxWidth="lg"
          fullWidth
          disableEnforceFocus
          disableRestoreFocus
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">
                  {selectedPatient?.patient.first_name} {selectedPatient?.patient.last_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  UHID: {selectedPatient?.patient.patient_code} &nbsp;|&nbsp;
                  {calculateAge(selectedPatient?.patient.date_of_birth)} yrs / {selectedPatient?.patient.gender}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button variant="outlined" size="small" startIcon={<Print />} onClick={() => setTodaysPrintDialog(true)}>
                  Print Today's Record
                </Button>
                <IconButton size="small" onClick={() => setDetailsDialog(false)}>
                  <Close />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {selectedPatient && (
              <Box>
                <Tabs
                  value={detailsTab}
                  onChange={handleDetailsTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                >
                  <Tab label="Patient Details" icon={<PersonAdd />} iconPosition="start" />
                  <Tab label={`Visit History (${selectedPatient.visits.length})`} icon={<Event />} iconPosition="start" />
                  <Tab label={`IPD Admissions (${selectedPatient.admissions.length})`} icon={<Hotel />} iconPosition="start" />
                  <Tab label="Consultations" icon={<LocalHospital />} iconPosition="start" />
                  <Tab label="Day Care" icon={<MedicalServices />} iconPosition="start" />
                  <Tab label="Invoices" icon={<Receipt />} iconPosition="start" />
                </Tabs>

                <Box sx={{ p: 2 }}>
                  {/* TAB 0 — Patient Details */}
                  {detailsTab === 0 && (
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, gap: 1 }}>
                        {editPatientMode ? (
                          <>
                            <Button size="small" startIcon={<Cancel />} onClick={() => setEditPatientMode(false)} disabled={editPatientLoading}>
                              Cancel
                            </Button>
                            <Button size="small" variant="contained" startIcon={editPatientLoading ? <CircularProgress size={14} color="inherit" /> : <Save />}
                              onClick={handleSavePatient} disabled={editPatientLoading}>
                              Save Changes
                            </Button>
                          </>
                        ) : (
                          <Button size="small" variant="outlined" startIcon={<Edit />} onClick={openEditPatient}>
                            Edit Details
                          </Button>
                        )}
                      </Box>

                      {editPatientMode ? (
                        <Grid container spacing={2}>
                          {/* Photo Section */}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                              {editPatientForm.photoBlob ? (
                                <Box sx={{ position: 'relative' }}>
                                  <Avatar src={editPatientForm.photoBlob} sx={{ width: 90, height: 90 }} variant="rounded" />
                                  <IconButton
                                    onClick={handleRemovePhoto}
                                    size="small"
                                    sx={{
                                      position: 'absolute', top: -8, right: -8,
                                      bgcolor: 'error.main', color: 'white',
                                      '&:hover': { bgcolor: 'error.dark' }
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Avatar sx={{ width: 90, height: 90, bgcolor: 'grey.200' }} variant="rounded">
                                  <PersonAdd sx={{ fontSize: 45, color: 'grey.400' }} />
                                </Avatar>
                              )}
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<PhotoCamera />}
                                onClick={() => setCameraOpen(true)}
                              >
                                {editPatientForm.photoBlob ? 'Retake Photo' : 'Take Photo'}
                              </Button>
                            </Box>
                          </Grid>

                          {/* Text Fields */}
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth size="small" label="First Name *"
                              value={editPatientForm.firstName}
                              onChange={e => setEditPatientForm(f => ({ ...f, firstName: e.target.value }))} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth size="small" label="Last Name *"
                              value={editPatientForm.lastName}
                              onChange={e => setEditPatientForm(f => ({ ...f, lastName: e.target.value }))} />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField fullWidth size="small" label="Phone *"
                              value={editPatientForm.phone}
                              onChange={e => setEditPatientForm(f => ({ ...f, phone: e.target.value }))} />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField fullWidth size="small" label="Father / Husband Name *"
                              value={editPatientForm.fatherHusbandName}
                              onChange={e => setEditPatientForm(f => ({ ...f, fatherHusbandName: e.target.value }))} />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField fullWidth size="small" label="Date of Birth *" type="date"
                              value={editPatientForm.dateOfBirth}
                              onChange={e => setEditPatientForm(f => ({ ...f, dateOfBirth: e.target.value }))}
                              InputLabelProps={{ shrink: true }} />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Gender *</InputLabel>
                              <Select value={editPatientForm.gender}
                                onChange={e => setEditPatientForm(f => ({ ...f, gender: e.target.value }))}
                                label="Gender *">
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField fullWidth size="small" label="Aadhar Number"
                              value={editPatientForm.aadharNumber}
                              onChange={e => {
                                const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                                setEditPatientForm(f => ({ ...f, aadharNumber: val }));
                              }}
                              inputProps={{ maxLength: 12 }}
                              helperText="12 digits"
                              error={editPatientForm.aadharNumber && editPatientForm.aadharNumber.length !== 12} />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField fullWidth size="small" label="Email"
                              value={editPatientForm.email}
                              onChange={e => setEditPatientForm(f => ({ ...f, email: e.target.value }))} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth size="small" label="Emergency Contact Name"
                              value={editPatientForm.emergencyContactName}
                              onChange={e => setEditPatientForm(f => ({ ...f, emergencyContactName: e.target.value }))} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth size="small" label="Emergency Contact Phone"
                              value={editPatientForm.emergencyContactPhone}
                              onChange={e => setEditPatientForm(f => ({ ...f, emergencyContactPhone: e.target.value }))} />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Address" multiline rows={2}
                              value={editPatientForm.address}
                              onChange={e => setEditPatientForm(f => ({ ...f, address: e.target.value }))} />
                          </Grid>

                          {/* Aadhar Upload Section */}
                          <Grid item xs={12}>
                            <Divider sx={{ my: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">AADHAR CARD IMAGES</Typography>
                                {scanResult && <Chip icon={<CheckCircle />} label="Data extracted" color="success" size="small" />}
                              </Box>
                            </Divider>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <input
                                  ref={frontInputRef}
                                  type="file"
                                  accept="image/*"
                                  hidden
                                  onChange={e => { handleAadharScan(e.target.files?.[0], 'front'); e.target.value = ''; }}
                                />
                                <Box
                                  sx={{
                                    border: '2px dashed', borderColor: aadharFrontPreview ? 'success.main' : 'primary.main',
                                    borderRadius: 1, p: 1.5, textAlign: 'center', cursor: 'pointer',
                                    bgcolor: aadharFrontPreview ? 'success.50' : 'background.paper',
                                    '&:hover': { bgcolor: 'action.hover' }
                                  }}
                                  onClick={() => frontInputRef.current?.click()}
                                >
                                  {aadharFrontPreview ? (
                                    <Box>
                                      <img src={aadharFrontPreview} alt="Aadhar Front" style={{ maxHeight: 80, maxWidth: '100%', borderRadius: 4 }} />
                                      {scanningFront && <CircularProgress size={16} sx={{ ml: 1 }} />}
                                    </Box>
                                  ) : (
                                    <Box>
                                      {scanningFront
                                        ? <CircularProgress size={24} />
                                        : <DocumentScanner sx={{ fontSize: 32, color: 'primary.main' }} />
                                      }
                                      <Typography variant="caption" display="block">
                                        {scanningFront ? 'Scanning...' : 'Upload Aadhaar Front'}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <input
                                  ref={backInputRef}
                                  type="file"
                                  accept="image/*"
                                  hidden
                                  onChange={e => { handleAadharScan(e.target.files?.[0], 'back'); e.target.value = ''; }}
                                />
                                <Box
                                  sx={{
                                    border: '2px dashed', borderColor: aadharBackPreview ? 'success.main' : 'primary.main',
                                    borderRadius: 1, p: 1.5, textAlign: 'center', cursor: 'pointer',
                                    bgcolor: aadharBackPreview ? 'success.50' : 'background.paper',
                                    '&:hover': { bgcolor: 'action.hover' }
                                  }}
                                  onClick={() => backInputRef.current?.click()}
                                >
                                  {aadharBackPreview ? (
                                    <Box>
                                      <img src={aadharBackPreview} alt="Aadhar Back" style={{ maxHeight: 80, maxWidth: '100%', borderRadius: 4 }} />
                                      {scanningBack && <CircularProgress size={16} sx={{ ml: 1 }} />}
                                    </Box>
                                  ) : (
                                    <Box>
                                      {scanningBack
                                        ? <CircularProgress size={24} />
                                        : <DocumentScanner sx={{ fontSize: 32, color: 'primary.main' }} />
                                      }
                                      <Typography variant="caption" display="block">
                                        {scanningBack ? 'Scanning...' : 'Upload Aadhaar Back'}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      ) : (
                        <Grid container spacing={2} alignItems="flex-start">
                          {/* Photo display */}
                          <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            {selectedPatient.patient.photo_blob ? (
                              <Avatar
                                src={selectedPatient.patient.photo_blob}
                                sx={{ width: 140, height: 140, boxShadow: 2, cursor: 'pointer' }}
                                variant="rounded"
                                onClick={() => setImageViewerDialog({
                                  open: true,
                                  src: selectedPatient.patient.photo_blob,
                                  title: 'Patient Photo'
                                })}
                              />
                            ) : (
                              <Avatar sx={{ width: 140, height: 140, bgcolor: 'grey.200' }} variant="rounded">
                                <PersonAdd sx={{ fontSize: 70, color: 'grey.400' }} />
                              </Avatar>
                            )}
                            <Typography variant="caption" color="text.secondary">Patient Photo</Typography>
                          </Grid>

                          {/* Info Fields Grid */}
                          <Grid item xs={12} md={9}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="text.secondary">UHID</Typography>
                                <Typography variant="body1" fontWeight="medium">{selectedPatient.patient.patient_code}</Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                                <Typography variant="body1" fontWeight="medium">
                                  {selectedPatient.patient.first_name} {selectedPatient.patient.last_name}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="text.secondary">Father / Husband Name</Typography>
                                <Typography variant="body1" fontWeight="medium">{selectedPatient.patient.father_husband_name || 'N/A'}</Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="text.secondary">Age / Gender</Typography>
                                <Typography variant="body1">
                                  {calculateAge(selectedPatient.patient.date_of_birth)} years / {selectedPatient.patient.gender}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                                <Typography variant="body1">{selectedPatient.patient.phone}</Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="text.secondary">Aadhar Number</Typography>
                                <Typography variant="body1">{selectedPatient.patient.aadhar_number || 'N/A'}</Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                                <Typography variant="body1">{selectedPatient.patient.email || 'N/A'}</Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>
                                <Typography variant="body1">
                                  {selectedPatient.patient.date_of_birth
                                    ? new Date(selectedPatient.patient.date_of_birth).toLocaleDateString('en-IN')
                                    : 'N/A'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="subtitle2" color="text.secondary">Emergency Contact</Typography>
                                <Typography variant="body1">
                                  {selectedPatient.patient.emergency_contact_name || 'N/A'} 
                                  {selectedPatient.patient.emergency_contact_phone ? ` (${selectedPatient.patient.emergency_contact_phone})` : ''}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                                <Typography variant="body1">{selectedPatient.patient.address || 'N/A'}</Typography>
                              </Grid>
                              {selectedPatient.patient.notes && (
                                <Grid item xs={12}>
                                  <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                                  <Typography variant="body1">{selectedPatient.patient.notes}</Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Grid>

                          {/* Aadhar Images display */}
                          {(selectedPatient.patient.aadhar_front_image || selectedPatient.patient.aadhar_back_image) && (
                            <Grid item xs={12}>
                              <Divider sx={{ my: 2 }}>
                                <Typography variant="caption" color="text.secondary">AADHAR CARD IMAGES</Typography>
                              </Divider>
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {selectedPatient.patient.aadhar_front_image && (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                    <img
                                      src={selectedPatient.patient.aadhar_front_image}
                                      alt="Aadhar Front"
                                      style={{ maxHeight: 100, borderRadius: 4, cursor: 'pointer', border: '1px solid #ddd' }}
                                      onClick={() => setImageViewerDialog({
                                        open: true,
                                        src: selectedPatient.patient.aadhar_front_image,
                                        title: 'Aadhar Front Image'
                                      })}
                                    />
                                    <Typography variant="caption" color="text.secondary">Aadhar Front</Typography>
                                  </Box>
                                )}
                                {selectedPatient.patient.aadhar_back_image && (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                    <img
                                      src={selectedPatient.patient.aadhar_back_image}
                                      alt="Aadhar Back"
                                      style={{ maxHeight: 100, borderRadius: 4, cursor: 'pointer', border: '1px solid #ddd' }}
                                      onClick={() => setImageViewerDialog({
                                        open: true,
                                        src: selectedPatient.patient.aadhar_back_image,
                                        title: 'Aadhar Back Image'
                                      })}
                                    />
                                    <Typography variant="caption" color="text.secondary">Aadhar Back</Typography>
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      )}
                    </Paper>
                  )}

                  {/* TAB 1 — Visit History */}
                  {detailsTab === 1 && (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell><strong>Visit Date</strong></TableCell>
                            <TableCell><strong>Type</strong></TableCell>
                            <TableCell><strong>Doctor</strong></TableCell>
                            <TableCell><strong>Diagnosis</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedPatient.visits.length === 0 ? (
                            <TableRow><TableCell colSpan={5} align="center">No visits found</TableCell></TableRow>
                          ) : (
                            selectedPatient.visits.map((visit, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>{new Date(visit.visit_date).toLocaleDateString('en-IN')}</TableCell>
                                <TableCell><Chip label={visit.visit_type?.toUpperCase()} size="small" /></TableCell>
                                <TableCell>{visit.doctor_name || 'N/A'}</TableCell>
                                <TableCell>{visit.consultation_diagnosis || visit.chief_complaint || 'N/A'}</TableCell>
                                <TableCell><Chip label={visit.status} size="small" color="primary" /></TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* TAB 2 — IPD Admissions */}
                  {detailsTab === 2 && (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell><strong>Admission Date</strong></TableCell>
                            <TableCell><strong>Room</strong></TableCell>
                            <TableCell><strong>Category</strong></TableCell>
                            <TableCell><strong>Planned Days</strong></TableCell>
                            <TableCell><strong>Daily Charge</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Discharge Date</strong></TableCell>
                            <TableCell align="center"><strong>Actions</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedPatient.admissions.length === 0 ? (
                            <TableRow><TableCell colSpan={8} align="center">No admissions found</TableCell></TableRow>
                          ) : (
                            selectedPatient.admissions.map((admission, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>{new Date(admission.admission_date).toLocaleDateString('en-IN')}</TableCell>
                                <TableCell>{admission.room_number}</TableCell>
                                <TableCell>{admission.category_name}</TableCell>
                                <TableCell>{admission.planned_days || 0}</TableCell>
                                <TableCell>₹{admission.daily_charge}</TableCell>
                                <TableCell>
                                  <Chip label={admission.status} size="small"
                                    color={admission.status === 'admitted' ? 'error' : 'success'} />
                                </TableCell>
                                <TableCell>
                                  {admission.discharge_date
                                    ? new Date(admission.discharge_date).toLocaleDateString('en-IN')
                                    : '—'}
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                    {admission.status === 'admitted' && isReception && (
                                      <>
                                        <Tooltip title="Extend Discharge Date">
                                          <IconButton size="small" color="primary" onClick={() => handleOpenExtendDialog(admission)}>
                                            <EventAvailable />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Discharge Patient">
                                          <IconButton size="small" color="error" onClick={() => handleOpenDischargeDialog(admission)}>
                                            <ExitToApp />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    )}
                                    {admission.status === 'admitted' && isDoctor && (
                                      <Tooltip title="Discharge Prescription">
                                        <IconButton size="small" color="secondary"
                                          onClick={() => navigate(`/doctor/discharge-prescription/${admission.id}`)}>
                                          <MedicalServices />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="View IPD Plans">
                                          <IconButton size="small" color="primary"
                                            onClick={() => navigate(`/reception/ipd-plans/${admission.id}`, {
                                              state: {
                                                patientCode: selectedPatient.patient.patient_code,
                                                patientName: `${selectedPatient.patient.first_name} ${selectedPatient.patient.last_name}`,
                                                roomNumber: admission.room_number,
                                                admissionDate: admission.admission_date,
                                                plannedDays: admission.planned_days || 0
                                              }
                                            })}>
                                            <Visibility />
                                          </IconButton>
                                        </Tooltip>
                                    <Tooltip title="Print Discharge Summary">
                                      <IconButton size="small" color="primary" onClick={() => handlePrintSummary(admission.id)}>
                                        <Print />
                                      </IconButton>
                                    </Tooltip>
                                    {admission.status === 'admitted' && (
                                      <Tooltip title="Print Daily Treatment & Diet Plan">
                                        <IconButton size="small" color="secondary"
                                          onClick={() => {
                                            setDailyPlanData(null);
                                            setDailyPlanDialog({ open: true, patientId: selectedPatient.patient.id, patientName: `${selectedPatient.patient.first_name} ${selectedPatient.patient.last_name}`, date: new Date().toISOString().split('T')[0] });
                                          }}>
                                          <EventNote />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* TAB 3 — Consultations */}
                  {detailsTab === 3 && (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell><strong>Date</strong></TableCell>
                            <TableCell><strong>Doctor</strong></TableCell>
                            <TableCell><strong>Visit Type</strong></TableCell>
                            <TableCell><strong>Diagnosis</strong></TableCell>
                            <TableCell align="center"><strong>Tx</strong></TableCell>
                            <TableCell align="center"><strong>Pkg</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell align="center"><strong>Action</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {!selectedPatient.consultations || selectedPatient.consultations.length === 0 ? (
                            <TableRow><TableCell colSpan={8} align="center">No consultations found</TableCell></TableRow>
                          ) : (
                            selectedPatient.consultations.map((c, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>
                                  <Typography variant="body2">{new Date(c.consultation_date).toLocaleDateString('en-IN')}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(c.consultation_date).toLocaleTimeString()}
                                  </Typography>
                                </TableCell>
                                <TableCell>{c.doctor_name}</TableCell>
                                <TableCell><Chip label={c.visit_type?.toUpperCase()} size="small" color="primary" /></TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {c.diagnosis || 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip label={c.prescribed_treatments_count || 0} size="small"
                                    color={c.prescribed_treatments_count > 0 ? 'success' : 'default'} />
                                </TableCell>
                                <TableCell align="center">
                                  <Chip label={c.prescribed_packages_count || 0} size="small"
                                    color={c.prescribed_packages_count > 0 ? 'info' : 'default'} />
                                </TableCell>
                                <TableCell>
                                  <Chip label={c.status || 'registered'} size="small"
                                    color={c.status === 'completed' ? 'success' : c.status === 'suggested_ipd' ? 'warning' : 'default'} />
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title={c.id ? "View Details" : "No consultation recorded"}>
                                    <span>
                                      <IconButton size="small" color="primary" onClick={() => handleViewConsultation(c.id)} disabled={!c.id}>
                                        <Visibility />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* TAB 4 — Day Care */}
                  {detailsTab === 4 && (
                    patientDetailDayCareLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                    ) : (
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell><strong>Start Date</strong></TableCell>
                              <TableCell align="right"><strong>Planned Days</strong></TableCell>
                              <TableCell align="right"><strong>Days Attended</strong></TableCell>
                              <TableCell align="right"><strong>Daily Charge</strong></TableCell>
                              <TableCell align="right"><strong>Total</strong></TableCell>
                              <TableCell><strong>Status</strong></TableCell>
                              <TableCell><strong>Discharge Date</strong></TableCell>
                              <TableCell><strong>Reason</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {patientDetailDayCare.length === 0 ? (
                              <TableRow><TableCell colSpan={8} align="center">No day care registrations found</TableCell></TableRow>
                            ) : (
                              patientDetailDayCare.map((dc, idx) => (
                                <TableRow key={idx} hover>
                                  <TableCell>{new Date(dc.start_date).toLocaleDateString('en-IN')}</TableCell>
                                  <TableCell align="right">{dc.planned_days}</TableCell>
                                  <TableCell align="right">{dc.days_attended}</TableCell>
                                  <TableCell align="right">₹{parseFloat(dc.daily_charge).toFixed(0)}</TableCell>
                                  <TableCell align="right">
                                    ₹{(parseFloat(dc.daily_charge) * parseInt(dc.days_attended)).toFixed(0)}
                                  </TableCell>
                                  <TableCell>
                                    <Chip label={dc.status} size="small"
                                      color={dc.status === 'active' ? 'success' : 'default'} />
                                  </TableCell>
                                  <TableCell>
                                    {dc.discharge_date
                                      ? new Date(dc.discharge_date).toLocaleDateString('en-IN')
                                      : '—'}
                                  </TableCell>
                                  <TableCell>{dc.reason || '—'}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )
                  )}

                  {/* TAB 5 — Invoices */}
                  {detailsTab === 5 && (
                    patientDetailBillsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                    ) : (
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell><strong>Bill No.</strong></TableCell>
                              <TableCell><strong>Type</strong></TableCell>
                              <TableCell><strong>Date</strong></TableCell>
                              <TableCell align="right"><strong>Amount</strong></TableCell>
                              <TableCell><strong>Payment</strong></TableCell>
                              <TableCell><strong>Status</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {patientDetailBills.length === 0 ? (
                              <TableRow><TableCell colSpan={6} align="center">No invoices found</TableCell></TableRow>
                            ) : (
                              patientDetailBills.map((bill, idx) => (
                                <TableRow key={idx} hover>
                                  <TableCell><Typography variant="body2" fontWeight="medium">{bill.bill_number}</Typography></TableCell>
                                  <TableCell>
                                    <Chip
                                      label={bill.bill_type === 'day_care' ? 'DAY CARE' : bill.bill_type.toUpperCase()}
                                      size="small"
                                      color={bill.bill_type === 'ipd' ? 'primary' : bill.bill_type === 'day_care' ? 'secondary' : 'default'}
                                    />
                                  </TableCell>
                                  <TableCell>{new Date(bill.bill_date).toLocaleDateString('en-IN')}</TableCell>
                                  <TableCell align="right"><strong>₹{parseFloat(bill.total_amount).toFixed(2)}</strong></TableCell>
                                  <TableCell>{bill.payment_method?.toUpperCase()}</TableCell>
                                  <TableCell>
                                    <Chip label={bill.status} size="small"
                                      color={bill.status === 'paid' ? 'success' : 'warning'} />
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* OPD Registration Dialog */}
        <Dialog open={opdDialog} onClose={() => setOpdDialog(false)} maxWidth="xl" fullWidth>
          <DialogTitle>
            Register OPD Visit
            {selectedPatientForAction && (
              <Alert severity="info" sx={{ mt: 1 }}>
                For patient: <strong>{selectedPatientForAction.first_name} {selectedPatientForAction.last_name}</strong> ({selectedPatientForAction.patient_code})
              </Alert>
            )}
          </DialogTitle>
          <DialogContent>
            <OPDRegistration
              preselectedPatient={selectedPatientForAction}
              onSuccess={() => {
                setOpdDialog(false);
                setSelectedPatientForAction(null);
                loadPatients();
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpdDialog(false);
              setSelectedPatientForAction(null);
            }}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* IPD Admission Dialog */}
        <Dialog open={ipdDialog} onClose={() => setIpdDialog(false)} maxWidth="xl" fullWidth>
          <DialogTitle>
            IPD Admission
            {selectedPatientForAction && (
              <Alert severity="info" sx={{ mt: 1 }}>
                For patient: <strong>{selectedPatientForAction.first_name} {selectedPatientForAction.last_name}</strong> ({selectedPatientForAction.patient_code})
              </Alert>
            )}
          </DialogTitle>
          <DialogContent>
            <IPDAdmission
              preselectedPatient={selectedPatientForAction}
              onSuccess={() => {
                setIpdDialog(false);
                setSelectedPatientForAction(null);
                loadPatients();
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setIpdDialog(false);
              setSelectedPatientForAction(null);
            }}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Print Dialog */}
        {printDialog && selectedAdmission && (
          <DischargeSummaryPrint
            admissionId={selectedAdmission}
            onClose={() => {
              setPrintDialog(false);
              setSelectedAdmission(null);
            }}
          />
        )}

        {/* Today's Record Print Dialog */}
        {todaysPrintDialog && selectedPatient && (
          <TodaysRecordPrint
            patientId={selectedPatient.patient.id}
            onClose={() => setTodaysPrintDialog(false)}
          />
        )}

        {/* Patient File Print Dialog */}
        {filePrintDialog.open && filePrintDialog.admissionId && (
          <PatientFilePrint
            admissionId={filePrintDialog.admissionId}
            patientId={filePrintDialog.patientId}
            onClose={() => setFilePrintDialog({ open: false, admissionId: null, patientId: null })}
          />
        )}
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {isReception || isDoctor ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: 64
                }
              }}
            >
              <Tab
                icon={<LocalHospital />}
                iconPosition="start"
                label="All Patients"
              />
              {isReception && (
                <Tab
                  icon={<PersonAdd />}
                  iconPosition="start"
                  label="Register New Patient"
                />
              )}
              <Tab
                icon={<Event />}
                iconPosition="start"
                label={isDoctor ? "My Consultations" : "All Consultations"}
              />
              {isReception && (
                <Tab
                  icon={<Hotel />}
                  iconPosition="start"
                  label="IPD Admissions"
                />
              )}
              {isReception && (
                <Tab
                  icon={<MedicalServices />}
                  iconPosition="start"
                  label="Day Care Admissions"
                />
              )}
            </Tabs>
          </Box>
          <Box>{renderTabContent()}</Box>
        </>
      ) : (
        renderPatientList()
      )}

      {/* Change Room Dialog - Available globally */}
      <Dialog open={changeRoomDialog.open} onClose={() => !changeRoomLoading && setChangeRoomDialog({ open: false, admission: null })} maxWidth="xs" fullWidth>
        <DialogTitle>
          Change Room
          {changeRoomDialog.admission && (
            <Typography variant="caption" display="block" color="text.secondary">
              {changeRoomDialog.admission.first_name} {changeRoomDialog.admission.last_name} — currently in Room {changeRoomDialog.admission.room_number}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {changeRoomRooms.length === 0 ? (
              <Typography color="text.secondary" variant="body2">No other available rooms found.</Typography>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Select New Room</InputLabel>
                <Select value={changeRoomSelectedId} onChange={e => setChangeRoomSelectedId(e.target.value)} label="Select New Room">
                  {changeRoomRooms.map(r => (
                    <MenuItem key={r.id} value={r.id}>
                      Room {r.room_number} — {r.category_name}
                      {r.current_occupancy < r.max_capacity
                        ? ` (${r.current_occupancy}/${r.max_capacity} occupied)`
                        : ' (Full)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeRoomDialog({ open: false, admission: null })} disabled={changeRoomLoading}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleChangeRoom}
            disabled={!changeRoomSelectedId || changeRoomLoading}
            startIcon={changeRoomLoading ? <CircularProgress size={16} color="inherit" /> : <MeetingRoom />}>
            Change Room
          </Button>
        </DialogActions>
      </Dialog>

      {/* Consultation Details Dialog - Available globally */}
      <Dialog
        open={consultationDialog}
        onClose={() => setConsultationDialog(false)}
        maxWidth="md"
        fullWidth
        style={{ zIndex: 1400 }}
      >
        <DialogTitle>
          Consultation Details
        </DialogTitle>
        <DialogContent>
          {selectedConsultation && (
            <Box sx={{ mt: 2 }}>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Patient</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedConsultation.consultation.first_name} {selectedConsultation.consultation.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedConsultation.consultation.patient_code}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Doctor</Typography>
                    <Typography variant="body1">
                      {selectedConsultation.consultation.doctor_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Consultation Date</Typography>
                    <Typography variant="body1">
                      {new Date(selectedConsultation.consultation.consultation_date).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    <Chip
                      label={selectedConsultation.consultation.status}
                      size="small"
                      color={selectedConsultation.consultation.status === 'completed' ? 'success' : 'warning'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Diagnosis</Typography>
                    <Typography variant="body1">
                      {selectedConsultation.consultation.diagnosis || 'N/A'}
                    </Typography>
                  </Grid>
                  {selectedConsultation.consultation.notes && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                      <Typography variant="body1">
                        {selectedConsultation.consultation.notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {selectedConsultation.treatments.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Prescribed Treatments ({selectedConsultation.treatments.length})
                  </Typography>
                  <TableContainer component={Paper} sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell>Treatment</TableCell>
                          <TableCell>Quantity</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Price</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedConsultation.treatments.map((treatment, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{treatment.name}</TableCell>
                            <TableCell>{treatment.quantity}</TableCell>
                            <TableCell>{treatment.duration} min</TableCell>
                            <TableCell>₹{treatment.price}</TableCell>
                            <TableCell>{treatment.notes || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {selectedConsultation.packages.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Prescribed Packages ({selectedConsultation.packages.length})
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell>Package</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Price</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedConsultation.packages.map((pkg, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{pkg.name}</TableCell>
                            <TableCell>{pkg.duration} days</TableCell>
                            <TableCell>₹{pkg.price}</TableCell>
                            <TableCell>{pkg.notes || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        {/* Hidden OPD Slip for printing */}
        <div style={{ display: 'none' }}>
          <div ref={consultationPrintRef}>
            {selectedConsultation && (
              <OPDSlipPrint
                patient={{
                  firstName: selectedConsultation.consultation.first_name,
                  lastName: selectedConsultation.consultation.last_name,
                  patientCode: selectedConsultation.consultation.patient_code,
                  phone: selectedConsultation.consultation.phone,
                  address: selectedConsultation.consultation.address,
                  gender: selectedConsultation.consultation.gender,
                  dateOfBirth: selectedConsultation.consultation.date_of_birth,
                  chiefComplaint: selectedConsultation.consultation.chief_complaint,
                }}
                visitInfo={{
                  opdId: consultationVisitData?.opd_id || selectedConsultation.consultation.opd_id || '',
                  yearlyId: consultationVisitData?.yearly_number || '',
                }}
                vitals={consultationVitals ? {
                  bloodPressure: consultationVitals.blood_pressure,
                  pulseRate: consultationVitals.pulse_rate,
                  respiratoryRate: consultationVitals.respiratory_rate,
                  temperature: consultationVitals.temperature,
                  height: consultationVitals.height,
                  weight: consultationVitals.weight,
                  bmi: consultationVitals.bmi,
                  oxygenSaturation: consultationVitals.oxygen_saturation,
                  painScale: consultationVitals.pain_scale,
                } : null}
                consultationInfo={{
                  doctorName: selectedConsultation.consultation.doctor_name || '',
                  diagnosis: selectedConsultation.consultation.diagnosis || '',
                  notes: selectedConsultation.consultation.notes || '',
                  treatments: (selectedConsultation.treatments || []).map(t => ({
                    treatmentName: t.name,
                    quantity: t.quantity
                  })),
                  packages: (selectedConsultation.packages || []).map(p => ({
                    packageName: p.name
                  }))
                }}
              />
            )}
          </div>
        </div>
        <DialogActions>
          <Button
            startIcon={<Print />}
            onClick={handlePrintConsultation}
            variant="outlined"
          >
            Print OPD Slip
          </Button>
          {selectedConsultation?.consultation.status === 'suggested_ipd' && isReception && (
            <Button
              startIcon={<Hotel />}
              onClick={handleIPDAdmissionFromConsultation}
              variant="contained"
              color="warning"
            >
              IPD Admission
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setConsultationDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Discharge Dialog */}
      <Dialog open={dischargeDialog.open} onClose={handleCloseDischargeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Discharge Patient</DialogTitle>
        <DialogContent>
          {dischargeDialog.admission && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Patient:</strong> {selectedPatient?.patient.patient_code} - {selectedPatient?.patient.first_name} {selectedPatient?.patient.last_name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Room:</strong> {dischargeDialog.admission.room_number}
              </Typography>
              <Typography variant="body2" gutterBottom sx={{ mb: 3 }}>
                <strong>Admission Date:</strong> {new Date(dischargeDialog.admission.admission_date).toLocaleDateString()}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Discharge Reason"
                value={dischargeDialog.dischargeReason}
                onChange={(e) => setDischargeDialog(prev => ({ ...prev, dischargeReason: e.target.value }))}
                placeholder="Enter reason for discharge (optional)"
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={dischargeDialog.isBlocked}
                    onChange={(e) => setDischargeDialog(prev => ({ ...prev, isBlocked: e.target.checked }))}
                    color="error"
                  />
                }
                label={
                  <Typography variant="body2">
                    Block patient (prevents future admissions)
                  </Typography>
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDischargeDialog} disabled={dischargeDialog.processing}>
            Cancel
          </Button>
          <Button
            onClick={handleDischarge}
            variant="contained"
            color="error"
            disabled={dischargeDialog.processing}
            startIcon={dischargeDialog.processing ? <CircularProgress size={20} /> : <ExitToApp />}
          >
            {dischargeDialog.processing ? 'Discharging...' : 'Discharge Patient'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend Planned Days Dialog */}
      <Dialog open={extendDialog.open} onClose={handleCloseExtendDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Extend Planned Days</DialogTitle>
        <DialogContent>
          {extendDialog.admission && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Patient:</strong> {selectedPatient?.patient.patient_code} - {selectedPatient?.patient.first_name} {selectedPatient?.patient.last_name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Room:</strong> {extendDialog.admission.room_number}
              </Typography>
              <Typography variant="body2" gutterBottom sx={{ mb: 3 }}>
                <strong>Current Planned Days:</strong> {extendDialog.admission.planned_days || 0}
              </Typography>

              <TextField
                fullWidth
                type="number"
                label="Additional Days"
                value={extendDialog.additionalDays}
                onChange={(e) => setExtendDialog(prev => ({ ...prev, additionalDays: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 1, max: 365 }}
                required
                helperText="Number of days to add to the current planned days"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason for Extension"
                value={extendDialog.reason}
                onChange={(e) => setExtendDialog(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Enter reason for extending planned days (optional)"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExtendDialog} disabled={extendDialog.processing}>
            Cancel
          </Button>
          <Button
            onClick={handleExtendDischarge}
            variant="contained"
            color="primary"
            disabled={extendDialog.processing || !extendDialog.additionalDays || extendDialog.additionalDays <= 0}
            startIcon={extendDialog.processing ? <CircularProgress size={20} /> : <EventAvailable />}
          >
            {extendDialog.processing ? 'Extending...' : 'Extend Planned Days'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>

      {/* Upload Test Reports Dialog */}
      <Dialog open={uploadReportDialog.open} onClose={() => !uploadReportDialog.uploading && setUploadReportDialog({ ...uploadReportDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Test Reports</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {uploadReportDialog.patient && (
            <Alert severity="info" sx={{ mb: 2 }}>
              For patient: <strong>{uploadReportDialog.patient.first_name} {uploadReportDialog.patient.last_name}</strong> ({uploadReportDialog.patient.patient_code})
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" startIcon={<CloudUpload />} fullWidth>
                Select Files
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
                  onChange={(e) => setUploadReportDialog({ ...uploadReportDialog, files: Array.from(e.target.files) })}
                />
              </Button>
              {uploadReportDialog.files.length > 0 && (
                <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
                  <Typography variant="caption">Selected: {uploadReportDialog.files.length} file(s)</Typography>
                  {uploadReportDialog.files.map((file, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {file.type.includes('pdf') ? <PictureAsPdf fontSize="small" color="error" /> : <Image fontSize="small" color="primary" />}
                      <Typography variant="caption">{file.name}</Typography>
                      <IconButton size="small" onClick={() => setUploadReportDialog({ ...uploadReportDialog, files: uploadReportDialog.files.filter((_, idx) => idx !== i) })}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Paper>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Report Type"
                value={uploadReportDialog.reportType}
                onChange={(e) => setUploadReportDialog({ ...uploadReportDialog, reportType: e.target.value })}
              >
                {['Blood Test', 'X-Ray', 'MRI', 'CT Scan', 'Ultrasound', 'ECG', 'General'].map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Test Date"
                value={uploadReportDialog.testDate}
                onChange={(e) => setUploadReportDialog({ ...uploadReportDialog, testDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={uploadReportDialog.notes}
                onChange={(e) => setUploadReportDialog({ ...uploadReportDialog, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadReportDialog({ ...uploadReportDialog, open: false })} disabled={uploadReportDialog.uploading}>Cancel</Button>
          <Button
            onClick={async () => {
              if (uploadReportDialog.files.length === 0) return;
              setUploadReportDialog({ ...uploadReportDialog, uploading: true });
              try {
                const formData = new FormData();
                formData.append('patientId', uploadReportDialog.patient.id);
                formData.append('reportType', uploadReportDialog.reportType);
                formData.append('testDate', uploadReportDialog.testDate);
                formData.append('notes', uploadReportDialog.notes);
                uploadReportDialog.files.forEach(file => formData.append('reports', file));
                await testReportsAPI.uploadReports(formData);
                setSuccess(`${uploadReportDialog.files.length} report(s) uploaded successfully`);
                setUploadReportDialog({ open: false, patient: null, files: [], reportType: 'General', testDate: new Date().toISOString().split('T')[0], notes: '', uploading: false });
              } catch (err) {
                setError(err.response?.data?.message || 'Failed to upload reports');
                setUploadReportDialog({ ...uploadReportDialog, uploading: false });
              }
            }}
            variant="contained"
            disabled={uploadReportDialog.files.length === 0 || uploadReportDialog.uploading}
          >
            {uploadReportDialog.uploading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Test Reports Dialog */}
      <Dialog open={viewReportsDialog.open} onClose={() => setViewReportsDialog({ open: false, patient: null, reports: [], loading: false })} maxWidth="md" fullWidth>
        <DialogTitle>
          Test Reports
          {viewReportsDialog.patient && ` - ${viewReportsDialog.patient.first_name} ${viewReportsDialog.patient.last_name}`}
        </DialogTitle>
        <DialogContent>
          {viewReportsDialog.loading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : viewReportsDialog.reports.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
              <Typography color="text.secondary">No test reports found</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>Type</TableCell>
                    <TableCell>Report Name</TableCell>
                    <TableCell>Test Date</TableCell>
                    <TableCell>Uploaded At</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewReportsDialog.reports.map((report) => (
                    <TableRow key={report.id} hover>
                      <TableCell><Chip label={report.report_type} size="small" color="primary" variant="outlined" /></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {report.mime_type.includes('pdf') ? <PictureAsPdf fontSize="small" color="error" /> : <Image fontSize="small" color="primary" />}
                          <Typography variant="body2">{report.report_name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{report.test_date ? new Date(report.test_date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>{new Date(report.uploaded_at).toLocaleDateString()}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={async () => {
                              try {
                                const response = await testReportsAPI.downloadReport(report.id);
                                const blob = new Blob([response.data], { type: report.mime_type });
                                const url = window.URL.createObjectURL(blob);
                                setReportViewerDialog({
                                  open: true,
                                  reportUrl: url,
                                  reportName: report.report_name,
                                  mimeType: report.mime_type
                                });
                              } catch (err) {
                                setError('Failed to view report');
                              }
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewReportsDialog({ open: false, patient: null, reports: [], loading: false })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Report Viewer Dialog */}
      <Dialog
        open={reportViewerDialog.open}
        onClose={() => {
          if (reportViewerDialog.reportUrl) {
            window.URL.revokeObjectURL(reportViewerDialog.reportUrl);
          }
          setReportViewerDialog({ open: false, reportUrl: null, reportName: '', mimeType: '' });
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{reportViewerDialog.reportName}</Typography>
            <IconButton
              size="small"
              onClick={() => {
                if (reportViewerDialog.reportUrl) {
                  window.URL.revokeObjectURL(reportViewerDialog.reportUrl);
                }
                setReportViewerDialog({ open: false, reportUrl: null, reportName: '', mimeType: '' });
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {reportViewerDialog.reportUrl && (
            reportViewerDialog.mimeType.includes('pdf') ? (
              <iframe
                src={reportViewerDialog.reportUrl}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title={reportViewerDialog.reportName}
              />
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', bgcolor: 'grey.100' }}>
                <img
                  src={reportViewerDialog.reportUrl}
                  alt={reportViewerDialog.reportName}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              const link = document.createElement('a');
              link.href = reportViewerDialog.reportUrl;
              link.download = reportViewerDialog.reportName;
              link.click();
            }}
          >
            Download
          </Button>
          <Button
            onClick={() => {
              if (reportViewerDialog.reportUrl) {
                window.URL.revokeObjectURL(reportViewerDialog.reportUrl);
              }
              setReportViewerDialog({ open: false, reportUrl: null, reportName: '', mimeType: '' });
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Daily plan dialog */}
      <Dialog open={dailyPlanDialog.open} onClose={() => setDailyPlanDialog(d => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Print Daily Treatment & Diet Plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Patient: <strong>{dailyPlanDialog.patientName}</strong>
          </Typography>
          <TextField
            fullWidth label="Date" type="date"
            value={dailyPlanDialog.date}
            onChange={e => setDailyPlanDialog(d => ({ ...d, date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDailyPlanDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button variant="contained" startIcon={dailyPlanLoading ? <CircularProgress size={16} color="inherit" /> : <Print />}
            onClick={fetchAndPrintDailyPlan} disabled={dailyPlanLoading}>
            {dailyPlanLoading ? 'Loading...' : 'Print'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Daily plan print portal */}
      {dailyPlanData && createPortal(
        <div style={{ position: 'fixed', top: 0, left: '-9999px', width: '190mm' }}>
          <div ref={dailyPlanPrintRef} style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#111', background: 'white', padding: '8mm 10mm' }}>
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #1b5e20', paddingBottom: 8, marginBottom: 10 }}>
              <img src="/logo.png" alt="MSG Logo" style={{ width: 48, height: 48, objectFit: 'contain', flexShrink: 0 }} />
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#1b5e20' }}>MSG NATUROPATHY CENTER</div>
                <div style={{ fontSize: 9, color: '#444' }}>Sirsa, Haryana &nbsp;|&nbsp; +91 7082999551 / 7082999351</div>
                <div style={{ fontSize: 12, fontWeight: 700, marginTop: 3 }}>PATIENT DAILY PLAN</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 16px', marginBottom: 10, fontSize: 10 }}>
              <div><strong>Patient:</strong> {dailyPlanData.patient.first_name} {dailyPlanData.patient.last_name}</div>
              <div><strong>UHID:</strong> {dailyPlanData.patient.patient_code}</div>
              <div><strong>Room:</strong> {dailyPlanData.admission?.room_number} ({dailyPlanData.admission?.category_name})</div>
              <div><strong>Date:</strong> {new Date(dailyPlanDialog.date).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ background: '#e3f2fd', padding: '4px 8px', fontWeight: 700, fontSize: 12, borderLeft: '4px solid #1565c0', marginBottom: 4 }}>Vitals ({(dailyPlanData.vitals||[]).length} records)</div>
              {(dailyPlanData.vitals||[]).length === 0 ? (
                <div style={{ color: '#888', fontSize: 10, padding: '6px 8px' }}>No vitals recorded for this date.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
                  <thead><tr style={{ background: '#f5f5f5' }}>{['Time','BP','Temp (°F)','Pulse','RR','SpO2%','Wt (kg)','RBS','Insulin (U)','Pain'].map(h => <th key={h} style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'left' }}>{h}</th>)}</tr></thead>
                  <tbody>{(dailyPlanData.vitals||[]).map((v, i) => (
                    <tr key={i} style={{ background: i%2===0?'#fff':'#fafafa' }}>
                      <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.recorded_at ? new Date(v.recorded_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.blood_pressure||'—'}</td>
                      <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.temperature||'—'}</td>
                      <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.pulse_rate||'—'}</td>
                      <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.respiratory_rate||'—'}</td>
                      <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.oxygen_saturation||'—'}</td>
                      <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.weight||'—'}</td>
                      <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.rbs||'—'}</td>
                      <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.insulin_units||'—'}</td>
                      <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.pain_scale!=null?v.pain_scale:'—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ background: '#e8f5e9', padding: '4px 8px', fontWeight: 700, fontSize: 12, borderLeft: '4px solid #2e7d32', marginBottom: 4 }}>Treatment Plan ({dailyPlanData.treatments.length} items)</div>
              {dailyPlanData.treatments.length === 0 ? (
                <div style={{ color: '#888', fontSize: 10, padding: '6px 8px' }}>No treatments scheduled.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                  <thead><tr style={{ background: '#f5f5f5' }}>{['Time Slot','Treatment','Assigned To'].map(h => <th key={h} style={{ border: '1px solid #ddd', padding: '3px 6px', textAlign: 'left' }}>{h}</th>)}</tr></thead>
                  <tbody>{dailyPlanData.treatments.map((t, i) => (
                    <tr key={i} style={{ background: i%2===0?'#fff':'#fafafa' }}>
                      <td style={{ border: '1px solid #ddd', padding: '3px 6px' }}>{t.time_slot}</td>
                      <td style={{ border: '1px solid #ddd', padding: '3px 6px', fontWeight: 600 }}>{t.treatment_name}</td>
                      <td style={{ border: '1px solid #ddd', padding: '3px 6px' }}>{t.assigned_to_name||'—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
            <div>
              <div style={{ background: '#fff8e1', padding: '4px 8px', fontWeight: 700, fontSize: 12, borderLeft: '4px solid #f57f17', marginBottom: 4 }}>Diet Plan ({dailyPlanData.meals.length} items)</div>
              {dailyPlanData.meals.length === 0 ? (
                <div style={{ color: '#888', fontSize: 10, padding: '6px 8px' }}>No diet plan scheduled.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                  <thead><tr style={{ background: '#f5f5f5' }}>{['Meal Time','Diet Item','Qty','Instructions'].map(h => <th key={h} style={{ border: '1px solid #ddd', padding: '3px 6px', textAlign: 'left' }}>{h}</th>)}</tr></thead>
                  <tbody>{dailyPlanData.meals.map((m, i) => (
                    <tr key={i} style={{ background: i%2===0?'#fff':'#fafafa' }}>
                      <td style={{ border: '1px solid #ddd', padding: '3px 6px' }}>{m.meal_time}</td>
                      <td style={{ border: '1px solid #ddd', padding: '3px 6px', fontWeight: 600 }}>{m.diet_item_name}</td>
                      <td style={{ border: '1px solid #ddd', padding: '3px 6px', textAlign: 'center' }}>{m.quantity}</td>
                      <td style={{ border: '1px solid #ddd', padding: '3px 6px', color: '#555' }}>{m.special_instructions||'—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
            <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 6, textAlign: 'center', fontSize: 9, color: '#888' }}>
              MSG Elite One World Wellness — Printed: {new Date().toLocaleString('en-IN')}
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Image Viewer Dialog */}
      <Dialog open={imageViewerDialog.open} onClose={() => setImageViewerDialog(d => ({ ...d, open: false }))} maxWidth="md">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="h6">{imageViewerDialog.title}</Typography>
          <IconButton onClick={() => setImageViewerDialog(d => ({ ...d, open: false }))}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', bgcolor: 'grey.100' }}>
          <img src={imageViewerDialog.src} alt={imageViewerDialog.title} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 4 }} />
        </DialogContent>
      </Dialog>

      {/* Camera Capture Dialog */}
      <CameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCameraCapture}
      />
    </Container>
  );
}

export default PatientList;
